import { isEmpty } from 'lodash';

import { Resource } from '../../../common/framework/enumeration/Resource';
import { getResourcesNew } from '../client/resource';
import { P_PAGE_SIZE } from '../../../common/framework/constants';

const labelMemoryStore: Map<string, Map<string, string>> = new Map<string, Map<string, string>>();

export async function getResourceLabels(rows: any[], idField: string, resource: Resource, resourceLabelField: string) {
    return await loadResourceLabels(resource.toString(), resourceLabelField, collectSpaceIds(rows, idField));
}

/**
 * Reads several fields of the same resource in one request, returning a label map per field.
 *
 * Calling `getResourceLabels` once per field would fetch the very same rows again for each one -
 * the response carries the whole resource regardless of which field is read out of it.
 */
export async function getResourceLabelsForFields(
    rows: any[],
    idField: string,
    resource: Resource,
    resourceLabelFields: string[],
): Promise<Map<string, Map<string, string>>> {
    return await loadResourceLabelsForFields(resource.toString(), resourceLabelFields, collectSpaceIds(rows, idField));
}

function collectSpaceIds(rows: any[], field: string) {
    const spaceIds = new Set<string>();
    for (const row of rows) {
        if (row[field]) {
            spaceIds.add(row[field]!!);
        }
    }
    return Array.from(spaceIds);
}

/**
 * Cached labels for one field of one resource.
 *
 * Keyed by resource *and* field: the same resource is legitimately asked for different fields (a
 * vehicle contract's registration number and its state, say), and a resource-only key served
 * whichever field happened to be fetched first.
 */
function labelCache(resource: string, field: string): Map<string, string> {
    const cacheKey = `${resource}|${field}`;
    if (!labelMemoryStore.has(cacheKey)) {
        labelMemoryStore.set(cacheKey, new Map<string, string>());
    }
    return labelMemoryStore.get(cacheKey)!!;
}

async function loadResourceLabels(resource: string, field: string, ids: string[]): Promise<Map<string, string>> {
    return (await loadResourceLabelsForFields(resource, [field], ids)).get(field)!!;
}

async function loadResourceLabelsForFields(
    resource: string,
    fields: string[],
    ids: string[],
): Promise<Map<string, Map<string, string>>> {
    const labelsByField = new Map<string, Map<string, string>>();
    const idsToLoadByField = new Map<string, string[]>();
    const idsToLoad = new Set<string>();

    for (const field of fields) {
        const cached = labelCache(resource, field);
        const labels = new Map<string, string>();
        const missing: string[] = [];

        for (const id of ids) {
            if (cached.has(id)) {
                labels.set(id, cached.get(id)!!);
            } else {
                missing.push(id);
                idsToLoad.add(id);
            }
        }

        labelsByField.set(field, labels);
        idsToLoadByField.set(field, missing);
    }

    // Load ids not found from memory store. One request covers every requested field, because the
    // response carries the whole resource either way.
    if (idsToLoad.size > 0) {
        const rows = await getResourcesNew(
            resource,
            -1,
            new Map<string, any>([
                // Only the ids the cache could not answer - passing every id defeated the cache.
                ['id', Array.from(idsToLoad)],
                [P_PAGE_SIZE, '10000'],
                ['page', '1'],
            ]),
        );

        const idsLoaded = new Set<string>();
        for (const row of rows) {
            const id = (row as any).id as string;
            idsLoaded.add(id);

            for (const field of fields) {
                const value = (row as any)[field] as string;
                // Mark rows with no label field content with ?.
                const label = isEmpty(value) ? '?' : value;
                labelCache(resource, field).set(id, label);
                labelsByField.get(field)!!.set(id, label);
            }
        }

        // Mark ids not loaded from database with -
        for (const field of fields) {
            for (const id of idsToLoadByField.get(field)!!) {
                if (!idsLoaded.has(id)) {
                    labelCache(resource, field).set(id, '-');
                    labelsByField.get(field)!!.set(id, '-');
                }
            }
        }
    }

    return labelsByField;
}
